import { type CommonAction, response } from "@iflyrpa/share";
import { z } from "zod";
import { ActionCommonParamsSchema, AxiosProxyParams } from "../../types";
import { Http } from "../../utils/http";
import { DouyinGetTopicsParamsSchema } from "./schema";

const { getABogus } = require("../../utils/douyin/douyinSafe/a_bogus.js");
const { generateCsrfTokenAdvanced } = require("../../utils/douyin/douyinSafe/csrfToken.js");

export type DouyinGetTopicsParams = z.infer<typeof DouyinGetTopicsParamsSchema>;
export { DouyinGetTopicsParamsSchema } from "./schema";

export interface Topic {
    group_id: string;
    cid: string;
    cha_name: string;
    tag: number;
    view_count: number;
}

export interface DouyinTopicsResponse {
    status_code: number;
    status_msg: string;
    sug_list?: Topic[];
    total?: number;
    data?: {
        sug_list?: Topic[];
        total?: number;
    };
}

export type DouyinGetTopicsAction = CommonAction<
    DouyinGetTopicsParams,
    {
        topics: Topic[];
        total: number;
    }
>;

export const douyinGetTopics: DouyinGetTopicsAction = async (_task, params) => {
    const userAgent =
        params.userAgent ||
        "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/134.0.0.0 Safari/537.38";

    const headers: any = {
        cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
        origin: "https://creator.douyin.com",
        referer: "https://creator.douyin.com/",
        Accept: "application/json, text/plain, */*",
        "Accept-Encoding": "gzip, deflate, br, zstd",
        "Content-Type": "application/json",
        pragma: "no-cache",
        "cache-control": "no-cache",
        "sec-ch-ua-platform": params.extraParam?.browserInfo?.["sec-ch-ua-platform"] || "",
        "sec-ch-ua": params.extraParam?.browserInfo?.["sec-ch-ua"] || "",
        "sec-ch-ua-mobile": "?0",
        "user-agent": userAgent,
        "sec-fetch-site": "same-origin",
        "sec-fetch-mode": "cors",
        "sec-fetch-dest": "empty",
        "accept-language": "zh-CN,zh;q=0.9",
        priority: "u=1, i",
    };

    const args: AxiosProxyParams = [{ headers }, _task.logger, params.proxyLoc, params.localIP, params.accountId];
    const http = new Http(...args);

    const csrfToken = generateCsrfTokenAdvanced({
        cookies: params.cookies,
        url: "https://creator.douyin.com/aweme/v1/search/challengesug/",
        method: "GET",
        userAgent,
    });

    const keyword = params.keyword || "热点";
    const topicParams = {
        keyword: keyword,
        source: "challenge_create",
        read_aid: "2906",
        aid: "1128",
        cookie_enabled: "true",
        screen_width: params.extraParam?.browserInfo?.screen_width || "1920",
        screen_height: params.extraParam?.browserInfo?.screen_height || "1080",
        browser_language: params.extraParam?.browserInfo?.browser_language || "zh-CN",
        browser_platform: params.extraParam?.browserInfo?.browser_platform || "Win32",
        browser_name: params.extraParam?.browserInfo?.browser_name || "Mozilla",
        browser_version: userAgent,
        browser_online: "true",
        timezone_name: "Asia/Shanghai",
        support_h265: "1",
        msToken: params.cookies.find((e) => e.name === "msToken")?.value || "",
        a_bogus: "",
    };
    const queryParamsBefore = new URLSearchParams(topicParams).toString();
    const aBogus = getABogus(
        `https://creator.douyin.com/aweme/v1/search/challengesug/?${queryParamsBefore}`,
        {},
        userAgent,
    );
    topicParams.a_bogus = aBogus;
    //生成query string
    const queryString = new URLSearchParams(topicParams).toString();
    console.log("抖音获取话题参数:", queryString);
    const res = await http.api<DouyinTopicsResponse>(
        {
            method: "get",
            url: `https://creator.douyin.com/aweme/v1/search/challengesug/?${queryString}`,
            headers: {
                ...headers,
                "X-secsdk-csrf-token": csrfToken,
            },
        },
        { retries: 3, retryDelay: 20, timeout: 3000 },
    );
    console.log("抖音获取话题响应:", res);

    const isSuccess = res.status_code === 0;
    const message = `抖音获取话题${isSuccess ? "成功" : `失败，原因：${res.status_msg}`}${_task.debug ? ` ${http.proxyInfo}` : ""}`;
    const data: { topics: Topic[]; total: number } = isSuccess
        ? { topics: res?.sug_list || [], total: res?.sug_list?.length || 0 }
        : { topics: [], total: 0 };

    return response(isSuccess ? 0 : 414, message, data);
};
